import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useFormikContext } from "formik"
import { compare } from "dinero.js"
import { Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { StandardLoadingButton } from "../../buttons/LoadingButton"
import { toCurrency, zeroDinero } from "../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../modules/currency/useMoney"

import { DeliveryTimesField, DynamicConfigKey, MeetingServiceTabValues } from "./form/formTypes"
import { calculateTotalAmountFromDeliveries } from "./drawer/orderSummary/helpers"

const SUBMIT_LOADING_TIMEOUT_MS = 2000

const useStyles = makeStyles()((_theme) => ({
    total: {
        display: "flex",
        alignItems: "end",
        flexWrap: "nowrap",
        gap: ".5rem",
    },
    submitButton: {
        justifyContent: "space-between",
        flexWrap: "nowrap",
        alignItems: "flex-start",
    },
}))
type ConfirmOrderSubmitButtonProps = Readonly<{
    isRefundOrder: boolean
    formId?: string
    handleSubmit?: () => void
}>

export function ConfirmOrderSubmitButton({ isRefundOrder, formId, handleSubmit }: ConfirmOrderSubmitButtonProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const { l10n } = useAppLocalization()

    const { values, isSubmitting, submitCount } = useFormikContext<MeetingServiceTabValues>()

    const [loading, setLoading] = useState<boolean>(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearLoadingTimer = useCallback(() => {
        if (!timerRef.current) return
        clearTimeout(timerRef.current)
        timerRef.current = null
    }, [])

    const setLoadingWithTimeout = useCallback(() => {
        clearLoadingTimer()
        setLoading(true)
        timerRef.current = setTimeout(() => {
            setLoading(false)
            timerRef.current = null
        }, SUBMIT_LOADING_TIMEOUT_MS)
    }, [clearLoadingTimer])

    useEffect(() => {
        if (!isSubmitting) {
            clearLoadingTimer()
            setLoading(false)
            return
        }

        setLoadingWithTimeout()

        return clearLoadingTimer
    }, [clearLoadingTimer, isSubmitting, setLoadingWithTimeout, submitCount])

    const deliveries: DeliveryTimesField | undefined = values[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const priceListIncludeVat = values["priceList"]?.item?.priceListIncludeVat ?? false

    const totalAmount = useMemo(
        () => deliveries && calculateTotalAmountFromDeliveries(deliveries.deliveryTimes),
        [deliveries?.deliveryTimes],
    )

    // Prevent submitting orders that contain only extra services (no regular menu items)
    const hasOnlyExtraServices = useMemo(() => {
        if (!deliveries) return false
        const hasAnyItems = deliveries.deliveryTimes.some((d) => d.orderLines.length > 0)
        if (!hasAnyItems) return false
        return !deliveries.deliveryTimes.some((d) => d.orderLines.some((line) => !line.isExtraService))
    }, [deliveries])

    const handleClick = useCallback(() => {
        if (formId && handleSubmit) {
            handleSubmit()
            const element = document.getElementById(formId)
            if (element instanceof HTMLFormElement) {
                const requestSubmit = (element as HTMLFormElement & { requestSubmit?: () => void }).requestSubmit
                if (requestSubmit) {
                    requestSubmit.call(element)
                    return
                }
            }
        }

        // Show a short loading indicator when the button is rendered outside the actual Formik <Form>.
        window.setTimeout(() => {
            setLoadingWithTimeout()
        }, 0)
    }, [formId, handleSubmit, setLoadingWithTimeout])

    const isTotalZero = useMemo(() => {
        if (!totalAmount) return true
        return compare(totalAmount, zeroDinero(toCurrency("DKK"))) === 0
    }, [totalAmount])

    const isRefundWithZeroTotal = isRefundOrder && isTotalZero

    const onlyExtraServicesMessage = l10n.getString("create-order-only-extra-services-error")

    return (
        <Tooltip title={hasOnlyExtraServices ? onlyExtraServicesMessage : ""} placement="top">
            <span>
                <StandardLoadingButton
                    loading={loading}
                    type={formId ? "button" : "submit"}
                    className={classes.submitButton}
                    onClick={formId ? handleClick : undefined}
                    disabled={hasOnlyExtraServices || isRefundWithZeroTotal}
                >
                    <LocalizedStrict id="create-order-confirm-order-button">
                        <Typography variant="inherit">Next</Typography>
                    </LocalizedStrict>
                    {totalAmount && (
                        <Typography variant="caption" textAlign="right" className={classes.total}>
                            <LocalizedStrict
                                id={priceListIncludeVat ? "create-order-vat-included" : "create-order-vat-excl"}
                            >
                                <Typography variant="caption">
                                    {priceListIncludeVat ? "(VAT incl.)" : "(VAT excl.)"}
                                </Typography>
                            </LocalizedStrict>
                            <Typography>
                                {isRefundOrder && "-"}
                                {moneyFactory.format(totalAmount)}
                            </Typography>
                        </Typography>
                    )}
                </StandardLoadingButton>
            </span>
        </Tooltip>
    )
}
